/**
 * How a person's name is chosen for display.
 *
 * **Client-side twin of the backend's `displayUserName`**
 * (`cgt-backend/src/common/user-display.ts`) and it must stay identical to it,
 * or one person reads two different ways on two screens.
 *
 * `username` IS NOT A NAME IN THIS DEPLOYMENT. The Cognito pool is
 * email-aliased, so `User.username` holds the account's sub UUID — real values
 * in the dev database look like `34485438-90d1-7058-9974-56b9ac1e49f5`. Using
 * it unguarded puts a raw id where a person's name belongs, which is worse than
 * the email it would replace. It stays in the chain only because a
 * locally-created account can carry a real handle there, and only behind the
 * same UUID guard the backend applies.
 *
 * Pure and dependency-free so it can be unit tested: vitest here is node-only
 * and matches `src/**\/*.test.ts`, so this logic left inline in a `.tsx` would
 * never be covered.
 */

/** Whatever subset of the name fields a given feed happens to send. */
export interface NamedUser {
  fullName?: string | null
  username?: string | null
  email?: string | null
}

/**
 * A username that is really an account id.
 *
 * Kept character-for-character in step with `UUIDISH` in the backend's
 * `user-display.ts` — the two deciding differently is the whole failure mode
 * this guard exists to prevent.
 */
const UUIDISH = /^[0-9a-f][0-9a-f-]{18,}$/i

/**
 * The best available name for a person.
 *
 * Blank strings count as absent, not as a name — an empty `fullName` is what a
 * half-provisioned account carries, and honouring it renders an empty cell.
 */
export function userDisplayName(user: NamedUser | null | undefined, fallback = ''): string {
  if (!user) return fallback
  const fullName = user.fullName?.trim()
  if (fullName) return fullName
  const username = user.username?.trim()
  if (username && !UUIDISH.test(username)) return username
  return user.email?.trim() || fallback
}

/**
 * Resolve an ACTOR STRING to a person's name.
 *
 * Audit and booking columns record `actorOf(user)` — the backend's
 * `email ?? username ?? sub` — so the stored value is a credential, not a name,
 * and it is a plain string with no user id attached. That is why this matches
 * against the roster rather than looking anything up: an equipment hold knows
 * WHO booked it only as the string it wrote at the time.
 *
 * Matched case-insensitively on email first, then on the roster's own display
 * name, so an actor recorded as a username still resolves when the roster
 * happens to name people that way. Returns null when nothing matches — the
 * caller decides whether to show the raw actor or a placeholder, because
 * "the person who left the company" and "no one" are different facts.
 */
export function resolveActorName(
  actor: string | null | undefined,
  roster: ReadonlyArray<{ name: string; email: string }>,
): string | null {
  const needle = actor?.trim().toLowerCase()
  if (!needle) return null
  const hit =
    roster.find((u) => u.email.trim().toLowerCase() === needle) ??
    roster.find((u) => u.name.trim().toLowerCase() === needle)
  // A roster row with a blank name is no better than the actor string itself.
  return hit?.name.trim() || null
}
